/**
 * WordPress dependencies
 */
import {
	Button,
	PanelRow,
	PanelBody,
	SelectControl,
	ToggleControl,
} from '@safe-wordpress/components';
import { useSelect, useDispatch } from '@safe-wordpress/data';
import { _x, sprintf } from '@safe-wordpress/i18n';
import { addQueryArgs } from '@safe-wordpress/url';

/**
 * External dependencies
 */
import { trim } from 'lodash';
import {
	ClipboardButton,
	FancyIcon,
	LoadingAnimation,
	PremiumFeature,
} from '@nab/components';
import {
	store as NAB_DATA,
	usePageAttribute,
	usePluginSetting,
} from '@nab/data';
import { getLetter } from '@nab/utils';
import type { ECommercePlugin, GoalWinner, Maybe } from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';
import {
	useAreUniqueResultsVisible,
	useExperimentAttribute,
	useHasUniqueResults,
	useMayHaveUniqueResults,
} from '../../hooks';
import { TypicalSampleSize } from '../typical-sample-size';

export const Result = (): JSX.Element => {
	const confidence = useConfidence();
	const goals = useExperimentAttribute( 'goals' ) || [];
	const isLoading = useIsLoadingResults();
	const ecommerce = useECommercePluginInActiveGoal();
	const segments = useExperimentAttribute( 'segments' ) || [];
	const homeUrl = useHomeUrl();
	const { setPublicResultStatus } = useDispatch( NAB_DATA );

	const mayHaveUniqueResults = useMayHaveUniqueResults();
	const hasUniqueResults = useHasUniqueResults();
	const subscription = usePluginSetting( 'subscription' );
	const canViewUniqueResults =
		subscription === 'professional' || subscription === 'enterprise';
	const isUniqueResultToggleVisible =
		( ! canViewUniqueResults && mayHaveUniqueResults ) ||
		( canViewUniqueResults && hasUniqueResults );

	const [ experimentId ] = usePageAttribute( 'editor/activeExperiment', 0 );

	const [ activeGoalId, setActiveGoal ] = usePageAttribute(
		'editor/activeGoal',
		goals[ 0 ]?.id ?? ''
	);
	const [ activeSegment, setActiveSegment ] = usePageAttribute(
		'editor/activeSegment',
		'default'
	);
	const [ areResultsUnique, setResultsUnique ] = useAreUniqueResultsVisible();
	const [ isPublicView ] = usePageAttribute( 'results/isPublicView', false );
	const [ isReadOnlyActive, setReadOnly ] = usePageAttribute(
		'results/isReadOnlyActive',
		false
	);
	const [ _, openExportModal ] = usePageAttribute(
		'results/isExportModalOpen',
		false
	);

	const goalOptions = goals.map( ( goal, index ) => {
		return {
			label:
				trim( goal.attributes.name ) ||
				getDefaulGoalNameForIndex( index ),
			value: goal.id,
		};
	} );

	let segmentOptions = segments.map( ( segment, index ) => {
		return {
			label:
				trim( segment.attributes.name ) ||
				getDefaulSegmentNameForIndex( index ),
			value: segment.id,
		};
	} );
	segmentOptions = [
		{
			label: _x( 'Combined Segment (Auto)', 'text', 'nelio-ab-testing' ),
			value: 'default',
		},
		...segmentOptions,
	];

	const summaryType = useSummaryType();
	const summaryText = useSummaryText();

	const publicResultUrl = addQueryArgs( homeUrl, {
		'nab-result': true,
		experiment: experimentId,
		preview: '',
	} );

	return (
		<PanelBody className="nab-experiment-result-summary">
			<PanelRow className="nab-experiment-result-summary__title">
				<span className="nab-experiment-result-summary__title-text">
					{ !! ecommerce
						? _x(
								'Conversion Rate Result',
								'text (experiment)',
								'nelio-ab-testing'
						  )
						: _x(
								'Result',
								'text (experiment)',
								'nelio-ab-testing'
						  ) }
				</span>
				{ 1 < goals.length && (
					<span className="nab-experiment-result-summary__goal-selector">
						<SelectControl
							label={ _x( 'Goal', 'text', 'nelio-ab-testing' ) }
							value={ activeGoalId }
							options={ goalOptions }
							onChange={ ( selectedGoalId ) =>
								setActiveGoal( selectedGoalId )
							}
						/>
					</span>
				) }
				{ 1 < segments.length && (
					<span className="nab-experiment-result-summary__segment-selector">
						<SelectControl
							label={ _x(
								'Segment',
								'text',
								'nelio-ab-testing'
							) }
							value={ activeSegment }
							options={ segmentOptions }
							onChange={ ( selectedSegment ) =>
								setActiveSegment( selectedSegment )
							}
						/>
					</span>
				) }
			</PanelRow>

			<PanelRow className="nab-experiment-result-summary__icon">
				{ 'loading' === summaryType ? (
					<LoadingAnimation
						text={ _x(
							'Loading results…',
							'text',
							'nelio-ab-testing'
						) }
					/>
				) : (
					<FancyIcon icon={ summaryType } isRounded />
				) }
			</PanelRow>

			{ 'loading' !== summaryType && (
				<PanelRow className="nab-experiment-result-summary__text">
					{ summaryText }
					<TypicalSampleSize type={ summaryType } />
				</PanelRow>
			) }

			{ 'winner' === summaryType && (
				<PanelRow className="nab-experiment-result-summary__confidence">
					{ sprintf(
						/* translators: %s: Confidence, as in "Confidence: 95%". */
						_x( 'Confidence: %s%%', 'text', 'nelio-ab-testing' ),
						`${ confidence }`
					) }
				</PanelRow>
			) }

			{ !! isUniqueResultToggleVisible && (
				<PanelRow className="nab-experiment-result-summary__uniqueness-selector">
					<PremiumFeature feature="unique-results">
						<ToggleControl
							label={ _x(
								'Unique results',
								'text',
								'nelio-ab-testing'
							) }
							checked={ canViewUniqueResults && areResultsUnique }
							onChange={ ( checked ) =>
								setResultsUnique( checked )
							}
						/>
					</PremiumFeature>
				</PanelRow>
			) }

			{ ! isPublicView && (
				<PanelRow className="nab-experiment-result-summary__uniqueness-selector">
					<ToggleControl
						label={ _x(
							'Public results',
							'text',
							'nelio-ab-testing'
						) }
						checked={ isReadOnlyActive }
						onChange={ ( checked ) => {
							void setPublicResultStatus( checked );
							setReadOnly( checked );
						} }
					/>
					{ !! isReadOnlyActive && (
						<div className="nab-experiment-result-summary__public-result">
							<div className="nab-experiment-result-summary__public-result-url">
								<input
									className="nab-experiment-result-summary__public-result-url-input"
									type="text"
									value={ publicResultUrl }
									readOnly
								/>
								<ClipboardButton text={ publicResultUrl } />
							</div>
						</div>
					) }
				</PanelRow>
			) }

			<PanelRow>
				<Button
					className="nab-experiment-result-summary__export-button"
					variant="secondary"
					onClick={ () => openExportModal( true ) }
					disabled={ isLoading }
				>
					{ _x( 'Export Results', 'command', 'nelio-ab-testing' ) }
				</Button>
			</PanelRow>
		</PanelBody>
	);
};

// =====
// HOOKS
// =====

type SummaryType =
	| 'loading'
	| 'winner'
	| 'possible-winner'
	| 'no-winner'
	| 'collecting-results';

function useSummaryType(): SummaryType {
	const minConfidence = usePluginSetting( 'minConfidence' );
	const minSampleSize = usePluginSetting( 'minSampleSize' );

	const isLoading = useIsLoadingResults();
	const pageViews = usePageViews();
	const winner = useWinningAlternative();
	const confidence = useConfidence();

	if ( isLoading ) {
		return 'loading';
	}

	if ( pageViews < minSampleSize ) {
		return 'collecting-results';
	}

	if ( ! winner ) {
		return 'no-winner';
	}

	return minConfidence <= confidence ? 'winner' : 'possible-winner';
}

function useSummaryText(): string {
	const type = useSummaryType();
	const winner = useWinningAlternative();
	const status = useExperimentAttribute( 'status' );

	switch ( type ) {
		case 'loading':
			return '';

		case 'collecting-results':
			return 'finished' !== status
				? _x( 'Not enough data yet', 'text', 'nelio-ab-testing' )
				: _x( 'Results inconclusive', 'text', 'nelio-ab-testing' );

		case 'no-winner':
			return 'finished' !== status
				? _x( 'No clear winner yet', 'text', 'nelio-ab-testing' )
				: _x( 'No clear winner', 'text', 'nelio-ab-testing' );

		case 'possible-winner':
			if ( ! winner ) {
				// NOTE. This should never happen.
				return '';
			}

			if ( 'control' === winner ) {
				return _x(
					'Control version leads',
					'text',
					'nelio-ab-testing'
				);
			}

			return sprintf(
				/* translators: %s: Variant id, as in "Variant B leads". */
				_x( 'Variant %s leads', 'text', 'nelio-ab-testing' ),
				getLetter( winner )
			);

		case 'winner':
			if ( ! winner ) {
				// NOTE. This should never happen.
				return '';
			}

			if ( 'control' === winner ) {
				return _x( 'Control version wins', 'text', 'nelio-ab-testing' );
			}

			return sprintf(
				/* translators: %s: Variant id, as in "Variant B wins". */
				_x( 'Variant %s wins', 'text', 'nelio-ab-testing' ),
				getLetter( winner )
			);
	}
}

const useHomeUrl = () =>
	useSelect(
		( select ) => select( NAB_DATA ).getPluginSetting( 'homeUrl' ),
		[]
	);

const useIsLoadingResults = () => {
	const [ activeExperiment ] = usePageAttribute(
		'editor/activeExperiment',
		0
	);
	return useSelect(
		( select ) =>
			! select( NAB_DATA ).hasFinishedResolution(
				'getExperimentResults',
				[ activeExperiment ]
			),
		[ activeExperiment ]
	);
};

const useECommercePluginInActiveGoal = () => {
	const [ expId ] = usePageAttribute( 'editor/activeExperiment', 0 );
	const goals = useExperimentAttribute( 'goals' ) || [];
	const [ goalId ] = usePageAttribute(
		'editor/activeGoal',
		goals[ 0 ]?.id ?? ''
	);
	return useSelect(
		( select ): Maybe< ECommercePlugin > => {
			const { getECommercePlugin } = select( NAB_DATA );
			return expId && goalId
				? getECommercePlugin( expId, goalId )
				: undefined;
		},
		[ expId, goalId ]
	);
};

const usePageViews = () => {
	const [ activeExperiment ] = usePageAttribute(
		'editor/activeExperiment',
		0
	);
	return useSelect(
		( select ) => select( NAB_DATA ).getPageViews( activeExperiment ) || 0,
		[ activeExperiment ]
	);
};

const useConfidence = () => useGoalWinner()?.confidence || 0;

const useWinningAlternative = (): Maybe< 'control' | number > => {
	const alternatives = useExperimentAttribute( 'alternatives' );
	const alternative = useGoalWinner()?.alternative;
	if ( undefined === alternative ) {
		return undefined;
	}
	const winAlt = alternatives?.[ alternative ];
	return winAlt?.id === 'control' ? 'control' : alternative;
};

const useGoalWinner = (): Maybe< GoalWinner > => {
	const [ experimentId ] = usePageAttribute( 'editor/activeExperiment', 0 );
	const goals = useExperimentAttribute( 'goals' ) || [];
	const [ activeGoalId ] = usePageAttribute(
		'editor/activeGoal',
		goals[ 0 ]?.id ?? ''
	);
	const winners = useSelect(
		( select ) => select( NAB_DATA ).getWinnersInExperiment( experimentId ),
		[ experimentId ]
	);
	return winners?.[ activeGoalId ];
};

// =======
// HELPERS
// =======

const getDefaulGoalNameForIndex = ( index: number ) =>
	index
		? sprintf(
				/* translators: %d: Goal index. */
				_x( 'Goal %d', 'text', 'nelio-ab-testing' ),
				index + 1
		  )
		: _x( 'Default Goal', 'text', 'nelio-ab-testing' );

const getDefaulSegmentNameForIndex = ( index: number ) =>
	sprintf(
		/* translators: %d: Segment index. */
		_x( 'Segment %d', 'text', 'nelio-ab-testing' ),
		index + 1
	);
